export const ACCOUNT_PSEUDONYM_MIN_LENGTH = 6;
export const ACCOUNT_PSEUDONYM_MAX_LENGTH = 20;
export const ACCOUNT_PSEUDONYM_PATTERN = '^[a-zA-Z0-9][a-zA-Z0-9_.\\-]{5,19}$';

export type AccountPseudonymValidationCode =
  | 'ACCOUNT_HANDLE_REQUIRED'
  | 'ACCOUNT_HANDLE_INVALID_LENGTH'
  | 'ACCOUNT_HANDLE_INVALID'
  | 'ACCOUNT_HANDLE_RESERVED';

const RESERVED_PSEUDONYMS = new Set([
  'about',
  'account',
  'accounts',
  'admin',
  'administrator',
  'anonymous',
  'api',
  'app',
  'article',
  'articles',
  'auth',
  'author',
  'authors',
  'billing',
  'blog',
  'bonus',
  'bot',
  'case',
  'chat',
  'contact',
  'cookie',
  'cookies',
  'copyright',
  'dashboard',
  'default',
  'delete',
  'dev',
  'docs',
  'documentation',
  'email',
  'feedback',
  'guest',
  'hello',
  'help',
  'home',
  'info',
  'login',
  'logout',
  'mail',
  'manage',
  'moderator',
  'news',
  'notice',
  'null',
  'official',
  'owner',
  'payment',
  'policy',
  'post',
  'posts',
  'privacy',
  'private',
  'public',
  'quaom',
  'read',
  'reader',
  'recover',
  'register',
  'root',
  'rules',
  'security',
  'service',
  'services',
  'settings',
  'signin',
  'signup',
  'staff',
  'status',
  'submit',
  'support',
  'system',
  'team',
  'terms',
  'test',
  'text',
  'texts',
  'translation',
  'translations',
  'undefined',
  'unknown',
  'user',
  'users',
  'wallet',
  'web',
  'world',
  'write',
  'writer',
  'www',
]);

const PROTECTED_PSEUDONYM_PARTS = new Set([
  'admin',
  'administrator',
  'api',
  'bot',
  'moderator',
  'official',
  'owner',
  'quaom',
  'root',
  'security',
  'staff',
  'support',
  'system',
  'team',
]);

export function normalizeAccountPseudonym(value: unknown): string {
  let raw = String(value || '').trim();
  try {
    if (typeof raw.normalize === 'function') raw = raw.normalize('NFKC');
  } catch {
    // Keep the raw value when ICU normalization is unavailable.
  }
  return raw.replace(/\s+/g, '').slice(0, ACCOUNT_PSEUDONYM_MAX_LENGTH + 16);
}

export function validateAccountPseudonym(
  value: unknown,
):
  | { ok: true; pseudonym: string; normalized: string }
  | { ok: false; code: AccountPseudonymValidationCode } {
  const pseudonym = normalizeAccountPseudonym(value);
  const normalized = pseudonym.toLowerCase();
  if (!normalized) return { ok: false, code: 'ACCOUNT_HANDLE_REQUIRED' };
  if (
    normalized.length < ACCOUNT_PSEUDONYM_MIN_LENGTH ||
    normalized.length > ACCOUNT_PSEUDONYM_MAX_LENGTH
  ) {
    return { ok: false, code: 'ACCOUNT_HANDLE_INVALID_LENGTH' };
  }
  if (normalized.includes('@') || !/^[a-z0-9][a-z0-9_.\-]{5,19}$/.test(normalized)) {
    return { ok: false, code: 'ACCOUNT_HANDLE_INVALID' };
  }
  if (/\.{2,}|_{2,}|-{2,}/.test(normalized)) return { ok: false, code: 'ACCOUNT_HANDLE_INVALID' };
  if (isReservedPseudonym(normalized)) return { ok: false, code: 'ACCOUNT_HANDLE_RESERVED' };
  return { ok: true, pseudonym, normalized };
}

export function isReservedPseudonym(normalizedPseudonym: string): boolean {
  const normalized = String(normalizedPseudonym || '').toLowerCase();
  const folded = normalized.replace(/[_.-]+/g, '');
  if (RESERVED_PSEUDONYMS.has(normalized) || RESERVED_PSEUDONYMS.has(folded)) return true;
  const parts = normalized.split(/[_.-]+/).filter(Boolean);
  return parts.some((part) => PROTECTED_PSEUDONYM_PARTS.has(part));
}
